//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';

import {
  getContentLibraryItems,
  getContentLibraryV2Items,
} from '@apis/content-library/contentLibraryApi';
import {
  CampaignSubTypeFilter,
  CampaignType,
  ContentLibraryV2RequestBody,
  IContentLibraryItemParams,
} from '@apis/content-library/contentLibraryApiModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { TWENTY_SECONDS_STALE_TIME } from '@queries/QueryOptionsConstants';
import { ErrorType, getErrorMessage } from '@utils/errorMessages';

const CONTENT_LIBRARY_LIMIT = 12;

export interface ContentItemsQueryParams {
  query: IContentLibraryItemParams;
}

export const useContentLibraryItemsInfiniteQuery = ({
  query,
}: ContentItemsQueryParams) => {
  const limit = query.limit ?? CONTENT_LIBRARY_LIMIT;

  const fetchContentLibraryItems = async (page = 0, signal?: AbortSignal) => {
    const isSamplingBrandSelected = query.filters?.campaignSubTypes?.includes(
      CampaignSubTypeFilter.SAMPLING_BRAND,
    );
    const isSamplingRetailerSelected =
      query.filters?.campaignSubTypes?.includes(
        CampaignSubTypeFilter.SAMPLING_RETAILER,
      );
    try {
      return await getContentLibraryItems(
        {
          ...query,
          filters: {
            ...query.filters,
            // Exclude sampling filter type if sampling subtypes selected as campaign
            // filter types are OR'd
            campaignTypes:
              isSamplingBrandSelected || isSamplingRetailerSelected
                ? query.filters?.campaignTypes?.filter(
                    (type) => type !== CampaignType.Sampling,
                  )
                : query.filters?.campaignTypes,
          },
          limit,
          offset: page > 0 ? (page - 1) * limit : 0,
        },
        { signal },
      );
    } catch (error) {
      throw new Error(getErrorMessage(ErrorType.MEDIA_ITEMS_FETCH, error), {
        cause: error,
      });
    }
  };

  return useInfiniteQuery({
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersMediaItems,
      query,
    ],
    queryFn: async ({ pageParam, signal }) =>
      await fetchContentLibraryItems(pageParam, signal),
    getNextPageParam: (last, pages) =>
      last.contentItems.length < limit && last.numAvailable === 0
        ? undefined
        : pages.length + 1,
    initialPageParam: 0,
    staleTime: TWENTY_SECONDS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

export interface ContentItemsV2Params {
  req: ContentLibraryV2RequestBody;
  enabled?: boolean;
}

export const contentLibraryV2ItemsInfiniteQueryOptions = (
  req: ContentLibraryV2RequestBody,
) => {
  const limit = req.limit ?? CONTENT_LIBRARY_LIMIT;
  const fetchContentLibraryItems = async (page = 0, signal?: AbortSignal) => {
    try {
      return await getContentLibraryV2Items(
        {
          ...req,
          limit,
          offset: page * limit,
        },
        { signal },
      );
    } catch (error) {
      throw new Error(getErrorMessage(ErrorType.MEDIA_ITEMS_FETCH, error), {
        cause: error,
      });
    }
  };

  return infiniteQueryOptions({
    queryFn: async ({ pageParam, signal }) =>
      await fetchContentLibraryItems(pageParam, signal),
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersMediaItems,
      QueryKeyConstants.MediaFiltersMediaItemsInfinite,
      req,
    ],
    getNextPageParam: (last, pages) =>
      last.contentItems.length < limit ? undefined : pages.length,
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    staleTime: TWENTY_SECONDS_STALE_TIME,
  });
};

export const useContentLibraryV2ItemsInfiniteQuery = ({
  enabled = true,
  req,
}: ContentItemsV2Params) => {
  return useInfiniteQuery({
    ...contentLibraryV2ItemsInfiniteQueryOptions(req),
    enabled,
  });
};

export const contentLibraryV2ItemsQueryOptions = (
  req: ContentLibraryV2RequestBody,
) =>
  queryOptions({
    queryFn: ({ signal }) => getContentLibraryV2Items(req, { signal }),
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersMediaItems,
      req,
    ],
    placeholderData: keepPreviousData,
    staleTime: TWENTY_SECONDS_STALE_TIME,
  });
